import React from 'react';
import { Loader } from '../../../components/Loader';
import { useSelector } from 'react-redux';
import sortByDateProp from '../../../lib/sortByDateProp';
import selectors from '../../../selectors/selectors.js';
import normalizeMessageType from '../../../lib/normalizeMessageType.js';
import { formatPhoneNumber } from '../../../lib/numUtils.js';
import { getLocationName } from '../../../lib/serviceProviderUtilities.js';
import getHumanMessageDate from '../../../lib/getHumanMessageDate.js';
import Message from './Message.js';
import setQuery from '../../../lib/setQuery.js';
import { formatDate } from '../../../lib/dateUtilities.js';
import MobileMessageWrapper from '../../../mobile/screens/inbox/MobileMessageWrapper.js';
import MobileFilterContainer from '../../../mobile/components/MobileFilterContainer.js';
import { getInboxMessages } from '../store/inboxScreenActions.js';
import inboxScreenFilters from '../lib/inboxScreenFilters.js';
import filterByUniqueKey from '../../../lib/filterByUniqueKey.js';
import getMessageSVG from '../../../lib/getMessageSVG.js';
import getMessageSnippet from 'lib/getMessageSnippet.js';
import getLeadMessageName from 'lib/getLeadMessageName.js';
import { putMessageStatus } from 'redux/actions/leadModalActions.js';
import { action } from 'lib/actionHelpers.js';
import { publishAction } from 'redux/actions/messagesActions.js';
import inboxFoldersSelector from 'screens/inbox/store/inboxFoldersSelector.js';

const CombinedMessages = ({ view, folderId }) => {
  const messages = useSelector(selectors.inboxCombinedMessages(folderId));

  const userId = useSelector((state) => (view === 'notificationsPanel' ? state.user.id : state.ui.filters.userId));
  const eid = useSelector(selectors.eid);
  const offset = useSelector(selectors.folderOffset(folderId));
  const sidebarLoading = useSelector((state) => state.inboxScreen.sidebarLoading);
  const readOrUnread = useSelector((state) => (state.ui.filters.showReadMessages ? 'read' : 'unread'));
  const search = String(useSelector((state) => state.ui.filters.search))
    .trim()
    .toLowerCase();
  let locations = useSelector((state) => state.currentServiceProvider.locations);
  const isMobile = useSelector(selectors.mobile);
  const folders = useSelector(inboxFoldersSelector);

  const activeFolder = folders.find((n) => n.id === folderId);
  const excludeUser = (activeFolder || {}).excludeUser === true;

  if (sidebarLoading && !isMobile) {
    return <Loader size={'lg'} container={true} type={'pulseCircle'} />;
  }

  if (messages.length === 0 && offset === 0 && !isMobile) {
    return null;
  }

  const eidMap = {};
  let filteredMessages = messages
    .sort(sortByDateProp('creationDatetime', activeFolder.sort === 'asc' ? 'asc' : 'desc'))
    .filter((n) => (eidMap[n.id] ? false : (eidMap[n.id] = true)))
    .filter((n) => (readOrUnread === 'read' ? n.is_read === '1' : n.is_read === '0'))
    .filter((n) => !(n.type === 'emails' && n.thread_id))
    .sort((n) => (n.rehydrated ? -1 : 1));

  filteredMessages = filterByUniqueKey(filteredMessages, 'thread_id', (n) => {
    return ['facebookmessenger', 'instagram'].includes((n.platform || {}).code);
  });

  const mappedMessages = filteredMessages
    .map((n) => {
      return {
        id: n.id,
        active: eid === n.id,
        leadId: n.lead_id,
        type: normalizeMessageType(n.type),
        svg: getMessageSVG(n),
        outgoing: n.outgoing,
        name: getLeadMessageName(n),
        serviceLocationName: getLocationName(n.service_location_id, locations),
        snippet: getMessageSnippet(n.message || n.sms_message),
        phone: formatPhoneNumber(n.caller_number),
        date: getHumanMessageDate(n.creationDatetime),
        fullDate: formatDate(n.creationDatetime, 'PPpp'),
        utmSource: n.utm_source,
        utmMedium: n.utm_medium,
        leadUrl: n.lead_url,
        landingUrl: n.landing_url,
        isRead: !!parseInt(n.is_read),
        statusId: n.status_id,
        threadMeta: n.thread_meta || {},
        message: n,
      };
    })
    .filter((n) => (!search ? n : String(n.message).toLowerCase().includes(search) || String(n.name).toLowerCase().includes(search)));

  const handleReadOrUnreadToggleClick = (e, message) => {
    if (e && e.preventDefault) {
      e.preventDefault();
      e.stopPropagation();
    }
    const data = {
      event: 'count',
      key: `updateReadUnreadStateConversations`,
      message: {
        count: {
          currentFolder: (message.message || {}).status_id,
          is_read: (message.message || {}).is_read ? '1' : '0',
        },
      },
    };
    const payload = { is_read: message.is_read === '1' ? '0' : '1', event_type: normalizeMessageType(message.type) };
    putMessageStatus(message.id, payload, () => {
      const actionName = payload.is_read === '0' ? 'PATCH_MESSAGE_AS_UNREAD' : 'PATCH_MESSAGE_AS_READ';
      action(actionName, message.uniqueId);
      publishAction({ data });
    });
  };

  const handleMessageClick = (e, message) => {
    const classes = e ? [...(e.target || {}).classList] : [];
    if (classes.includes('mark-as') || classes.includes('option')) {
      return;
    }
    setQuery({
      lead: message.leadId || null,
      eid: message.id || null,
      etype: message.type || null,
      tid: !message.leadId ? ((message || {}).message || {}).thread_id : null,
      modal: 'true',
      tab: 'basic',
      pipelineLeadId: null,
      statusId: null,
      type: null,
    });
  };

  return (
    <>
      <div className="inbox-messages">
        {isMobile && (
          <MobileFilterContainer filters={inboxScreenFilters({ excludeUser })} onReload={(cb) => getInboxMessages({ callback: cb })}>
            {isMobile && mappedMessages.map((message, idx) => <MobileMessageWrapper key={idx} message={message} onClick={(e) => handleMessageClick(e, message)} onReadOrUnreadToggleClick={handleReadOrUnreadToggleClick} />)}
          </MobileFilterContainer>
        )}
        {!isMobile && mappedMessages.map((message, idx) => <Message key={idx} message={message} onClick={(e) => handleMessageClick(e, message)} onReadOrUnreadToggleClick={handleReadOrUnreadToggleClick} />)}
      </div>
    </>
  );
};

export default CombinedMessages;
